/** What the Money screens share: the API shapes, who may do what, a fetch hook. */
import { useCallback, useEffect, useRef, useState } from 'react';
import { can, role, type Scope } from '@cider/domain/access';
import { FestivalClock } from '@cider/domain/engines';
import { formatCount, useLive } from '@cider/ui';
import { api, sentence } from '../../shell/api.js';
import { usePhase } from '../../shell/phase.js';
import { personOf, type Me } from '../../shell/session.js';

export interface Account {
  id: string;
  number: string;
  name: string;
  type: 'asset' | 'liability' | 'equity' | 'income' | 'expense';
  parentId: string | null;
  archivedAt: string | null;
  /** Null while it waits on a director (ADR 0036, ruling 1). */
  approvedAt?: string | null;
  /** QuickBooks' own key on an imported account; `cider:` on one made here. */
  qboId?: string | null;
  areaId: string | null;
  role: string | null;
}
export interface DraftLine {
  accountId: string;
  debitCents: number;
  creditCents: number;
  memo?: string | null;
}
export interface Draft {
  id: string;
  entryDate: string;
  memo: string;
  state: 'draft' | 'submitted' | 'approved' | 'posted' | 'declined';
  lines: DraftLine[];
  createdBy: string | null;
  approvedBy: string | null;
  postedEntryId: string | null;
  createdAt: string;
}
export interface Entry {
  id: string;
  entryDate: string;
  fiscalYear: number;
  memo: string;
  source: string;
  postedBy: string;
  approvedBy: string;
  reversesId: string | null;
  reversalReason?: string | null;
  createdAt: string;
  /** The entry's size, the sum of its debits — on the list, not on a single entry's read. */
  amountCents?: number;
}
export interface EntryLine {
  id: string;
  lineNo: number;
  accountId: string;
  debitCents: number;
  creditCents: number;
  memo: string | null;
}

export function doors(me: Me): { view: boolean; post: boolean; approve: boolean; close: boolean } {
  const p = personOf(me);
  return {
    view: can(p, 'finance.view').allowed,
    post: can(p, 'finance.post', { write: true }).allowed,
    approve: can(p, 'finance.approve', { write: true }).allowed,
    close: can(p, 'finance.close', { write: true }).allowed,
  };
}

/** The fiscal year today: Jul 1 – Jun 30, named for the year it ends in. */
export function fiscalYearToday(): number {
  const w = FestivalClock.wallClock(new Date());
  return w.month >= 7 ? w.year + 1 : w.year;
}

/**
 * **The Budget's year is a festival** (James, Oct 14 — ruling 6): every Budget screen names the
 * festival it budgets — "the 2027 festival" — and the head says once which fiscal year its
 * baseline reads: "FY2027 · the 2026 festival". A budget's fiscal year holds the festival of the
 * year before (FY2028 runs July 2027 to June 2028), so the 2027 festival is FY2028's budget.
 */
export const festivalOf = (fy: number): number => fy - 1;
export const theFestival = (fy: number): string => `the ${String(festivalOf(fy))} festival`;
export const budgetEyebrow = (fy: number): string =>
  `MONEY · ASK · APPROVE · ${theFestival(fy).toUpperCase()} · BASELINE FY${String(fy - 1)} · THE ${String(fy - 2)} FESTIVAL`;

/**
 * Which budget a Budget screen opens on: `?fy=` when the address names one; otherwise the
 * festival being budgeted, from the phase engine — after a festival's closeout (and the
 * onboarding after it) that is next year's; until then, this year's. With no festival dates
 * set it is this fiscal year's, as before.
 */
export function budgetYearAhead(phase: { id: string | null; festivalYear: number | null }): number {
  if (phase.id === null || phase.festivalYear === null) return fiscalYearToday();
  const ahead =
    phase.id === 'closeout' || phase.id === 'onboarding'
      ? phase.festivalYear + 1
      : phase.festivalYear;
  return ahead + 1;
}

export function useBudgetYear(): {
  fy: number;
  setFy: (fy: number) => void;
  /** The budgets the screen offers: the one ahead, and the years behind it. */
  years: (count: number) => number[];
} {
  const phase = usePhase();
  const named = Number(new URLSearchParams(window.location.search).get('fy')) || null;
  const ahead = budgetYearAhead(phase);
  const [chosen, setFy] = useState<number | null>(named);
  const fy = chosen ?? ahead;
  return {
    fy,
    setFy,
    years: (count) => Array.from({ length: count }, (_, i) => Math.max(ahead, fy) + 1 - i),
  };
}

export interface Loaded<T> {
  data: T | null;
  error: string | null;
  loading: boolean;
  reload: () => void;
  /** Live updates (ADR 0008): a change arrived while the screen asked to hold; Reload or Keep mine. */
  stale: boolean;
  keepMine: () => void;
}

/** The live channels a path listens to: the module its first segment names. */
export const channelsFor = (path: string): string[] => {
  const first = path.replace(/^\//, '').split(/[/?]/)[0] ?? '';
  return first ? [first] : [];
};
export const EVENTS_URL = '/api/events';

/**
 * Load a path, and keep it current: a change on its module, or the connection coming back,
 * re-fetches within a moment. With `hold` (a form in progress) the screen is not overwritten —
 * `stale` turns on and the screen offers Reload / Keep mine.
 */
export function useLoad<T>(
  /** `null` while the screen has nothing to ask for yet — it waits, and says nothing. */
  path: string | null,
  options: { hold?: boolean } = {},
): Loaded<T> {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [tick, setTick] = useState(0);
  const [stale, setStale] = useState(false);
  const reload = useCallback(() => {
    setStale(false);
    setTick((t) => t + 1);
  }, []);
  const keepMine = useCallback(() => {
    setStale(false);
  }, []);
  const loadedPath = useRef<string | null>(null);
  const hold = options.hold === true;
  const timer = useRef<number | null>(null);
  useLive(EVENTS_URL, channelsFor(path ?? ''), () => {
    if (hold) {
      setStale(true);
      return;
    }
    if (timer.current !== null) window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => {
      timer.current = null;
      setTick((t) => t + 1);
    }, 400);
  });
  useEffect(() => {
    let live = true;
    if (path === null) {
      setLoading(false);
      return;
    }
    setLoading(true);
    // A new path is a new thing: the old answer never shows under the new question.
    if (loadedPath.current !== path) {
      loadedPath.current = path;
      setData(null);
    }
    api<T>('GET', path)
      .then((d) => {
        if (live) {
          // The same answer is the same object: a live re-fetch that changed nothing re-renders nothing
          // (a form initialized from the data keeps what was typed).
          setData((cur) => (cur !== null && JSON.stringify(cur) === JSON.stringify(d) ? cur : d));
          setError(null);
        }
      })
      .catch((e: unknown) => {
        if (live) setError(sentence(e));
      })
      .finally(() => {
        if (live) setLoading(false);
      });
    return () => {
      live = false;
    };
  }, [path, tick]);
  return { data, error, loading, reload, stale, keepMine };
}

/**
 * The words a cell prints for a row whose name did not come with it.
 *
 * This used to be eight characters of the id. James, from the staging box, Sep 27: *Successors
 * shows "Area chair / co-chair · area af96ef1e" instead of the area's name.* A uuid fragment is
 * not a shorter name — it tells a reader nothing, it cannot be searched for, and it reads as a
 * fault. So a screen prints the name it was given, or it says in words that there isn't one.
 *
 * `tools/no-ids-on-screens.test.ts` reads `apps/web/src` on every push and refuses an id
 * rendered into any screen.
 */
export const unnamed = (what: string, id?: string | null): string =>
  id ? `a ${what} no longer here` : '—';

const SYSTEM = '00000000-0000-0000-0000-000000000000';

/**
 * A names cache behind one route: `who(id)` answers from the cache and asks the API, in one
 * batched call, for what it does not have. Money asks `/money/names`; People asks `/people/names`.
 */
export function makeNames(path: string): () => (id: string | null | undefined) => string {
  const names = new Map<string, string>();
  let wanted = new Set<string>();
  let timer: ReturnType<typeof setTimeout> | null = null;
  const listeners = new Set<() => void>();
  const ask = (): void => {
    if (timer) return;
    timer = setTimeout(() => {
      timer = null;
      const ids = [...wanted];
      wanted = new Set();
      if (ids.length === 0) return;
      void api<{ names: Record<string, string> }>('GET', `${path}?ids=${ids.join(',')}`)
        .then((r) => {
          for (const id of ids) names.set(id, r.names[id] ?? 'someone (no account)');
          for (const l of listeners) l();
        })
        .catch(() => {
          for (const id of ids) names.set(id, 'someone');
          for (const l of listeners) l();
        });
    }, 10);
  };
  return function useNames() {
    const [, bump] = useState(0);
    useEffect(() => {
      const l = (): void => {
        bump((n) => n + 1);
      };
      listeners.add(l);
      return () => {
        listeners.delete(l);
      };
    }, []);
    return (id) => {
      if (!id) return '—';
      if (id === SYSTEM) return 'the system';
      const have = names.get(id);
      if (have) return have;
      if (!/^[0-9a-f-]{36}$/.test(id)) return id;
      wanted.add(id);
      ask();
      return '…';
    };
  };
}

/**
 * A person's display name on a Money screen — People's name, the sign-in email until People
 * knows them, never an id fragment.
 */
export const usePeople = makeNames('/money/names');

/** One action against the API: busy while it runs, a sentence if it refuses, a reload after. */
/**
 * The rule for every drawn action: one the caller cannot take is either not drawn or, drawn for
 * context, says the guard's sentence and who to ask when clicked — never silence. This is that
 * sentence, from the same policy the guard runs.
 */
export function refusalFor(me: Me, capability: string, scope: Scope = {}): string | null {
  const d = can(personOf(me), capability, { write: true, ...scope });
  if (d.allowed) return null;
  const ask = role(d.askRole);
  return d.reason.includes('Ask ') ? d.reason : `${d.reason} Ask a ${ask.name.toLowerCase()}.`;
}

/**
 * Every action answers (Walk fixes 1). `act` keeps what the festival said about what just
 * happened — the `says` sentence the API returns — so a screen can put the answer beside the
 * button that was pressed instead of leaving a save to look like nothing at all. A refusal is
 * the same thing in the other direction: `refusal` says why, in the same place.
 */
export function useAct(reload: () => void): {
  busy: boolean;
  refusal: string | null;
  /** What the festival said about the last action, when it said anything. */
  said: string | null;
  act: (fn: () => Promise<unknown>, after?: () => void) => Promise<void>;
  /** Say a refusal without asking the API — for an action drawn for context that this caller cannot take. */
  refuse: (says: string) => void;
  clear: () => void;
} {
  const [busy, setBusy] = useState(false);
  const [refusal, setRefusal] = useState<string | null>(null);
  const [said, setSaid] = useState<string | null>(null);
  const act = useCallback(
    async (fn: () => Promise<unknown>, after?: () => void): Promise<void> => {
      setBusy(true);
      setRefusal(null);
      setSaid(null);
      try {
        const answer = await fn();
        const says =
          typeof answer === 'object' && answer !== null && 'says' in answer
            ? (answer as { says?: unknown }).says
            : null;
        if (typeof says === 'string' && says.trim() !== '') setSaid(says);
        reload();
        after?.();
      } catch (e) {
        setRefusal(sentence(e));
      } finally {
        setBusy(false);
      }
    },
    [reload],
  );
  const clear = useCallback(() => {
    setRefusal(null);
    setSaid(null);
  }, []);
  const refuse = useCallback((says: string) => {
    setSaid(null);
    setRefusal(says);
  }, []);
  return { busy, refusal, said, act, refuse, clear };
}

/** The payables doors, beyond the ledger's. */
export function payDoors(me: Me): { order: boolean; pay: boolean; sign: boolean } {
  const p = personOf(me);
  return {
    order: can(p, 'finance.order', { write: true }).allowed,
    pay: can(p, 'finance.pay', { write: true }).allowed,
    sign: can(p, 'finance.sign', { write: true }).allowed,
  };
}

export interface Vendor {
  id: string;
  name: string;
  junk: boolean;
  /** Null is not recorded: a vendor from 1.0, which kept no 1099 flag (James, Oct 19). */
  ten99: boolean | null;
  archivedAt: string | null;
  /** Why it was deactivated (James, Oct 13) — it keeps its history and leaves the pickers. */
  archivedReason?: string | null;
  /** Who deactivated it. */
  archivedBy?: string | null;
  qboId: string | null;
  personId: string | null;
  /** The People organization behind the vendor (defect #51); the name and address come through the port. */
  organizationId?: string | null;
  contactName?: string | null;
  /** The vendor's own line, on its contact card — never a person's key. */
  contactPhone?: string | null;
  email?: string | null;
  /** The 1.0 lists the vendor came from, and 1.0's W-9 answer, yes or no (the vendors import). */
  one?: { lists: string[]; w9OnFile: boolean | null } | null;
  /** The mailing address a check goes to (M3): all four, or the check waits. */
  addressStreet?: string | null;
  addressCity?: string | null;
  addressState?: string | null;
  addressZip?: string | null;
}
/** What the payee picker offers: People's persons and organizations, each with the vendor it already is. */
export interface Payee {
  kind: 'organization' | 'person';
  id: string;
  name: string;
  email: string | null;
  kinds: string[];
  vendorId: string | null;
}
export interface Doc {
  name: string;
  kind: 'photo' | 'invoice' | 'statement' | 'receipt';
}
/** One line of a purchase order (Standard v2 §2.1): what · qty · unit · unit price · account. */
export interface OrderLine {
  description: string;
  quantity: number;
  unitCents: number;
  unit?: string | null;
  accountId?: string | null;
  areaId?: string | null;
}
export interface Order {
  id: string;
  number: string | null;
  vendorId: string;
  accountId: string;
  areaId: string | null;
  what: string;
  lines: OrderLine[];
  totalCents: number;
  state: 'draft' | 'asked' | 'approved' | 'ordered' | 'received' | 'billed' | 'paid' | 'canceled';
  askedBy?: string | null;
  shipTo?: string | null;
  neededBy?: string | null;
  createdBy: string | null;
  approvedBy: string | null;
  overageCents?: number | null;
  sentAt: string | null;
  canceledReason: string | null;
  /** A standing contract: the finance director's PO on a FIXED line (James, Oct 17). */
  standing?: boolean;
  createdAt: string;
}
export interface Bill {
  id: string;
  vendorId: string;
  purchaseOrderId: string | null;
  /** What the order said, and how far the bill is from it (Track 1: PO → invoice variance). */
  againstOrder?: { number: string | null; orderCents: number; diffCents: number } | null;
  invoiceNumber: string | null;
  billDate: string;
  dueDate: string | null;
  what: string;
  lines: { accountId: string; areaId?: string | null; cents: number; memo?: string | null }[];
  totalCents: number;
  document: Doc | null;
  state: 'bill' | 'approved' | 'in the queue' | 'printed' | 'cleared' | 'voided' | 'reissued';
  createdBy: string | null;
  approvedBy: string | null;
  /** The second director, at or over the year's amount (Money Part 4). */
  approvedBy2: string | null;
  /** Set where the bill arrived before anything was received against its order. */
  beforeReceipt: string | null;
  declinedReason: string | null;
  postedEntryId: string | null;
  checkNumber: number | null;
  duplicateOfId: string | null;
  duplicateClearedBy: string | null;
  /** How many directors it needs and how many it has — from the engine, never counted here. */
  approval: { need: 1 | 2; have: number; enough: boolean; says: string };
}
export interface Credit {
  id: string;
  vendorId: string;
  cents: number;
  reason: string;
  accountId: string;
  appliedBillId: string | null;
}
export interface Check {
  number: number;
  stage:
    | 'in the queue'
    | 'printed'
    | 'awaiting second signature'
    | 'signed'
    | 'mailed'
    | 'handed'
    | 'cleared'
    | 'voided';
  signers: string[];
  printedPages: number | null;
  voidReason: string | null;
  reissuedAs: number | null;
  runId: string | null;
  vendorId: string | null;
  payee: string | null;
  cents: number | null;
  billIds: string[];
  paymentType: string | null;
  issuedAt: string;
  reissues: number | null;
}
export interface Template {
  id: string;
  name: string;
  vendorId: string;
  makes: 'bill' | 'order';
  onDate: string;
  cents: number;
  accountId: string;
  what: string;
  active: boolean;
  lastMadeFiscalYear: number | null;
}
export interface Expense {
  id: string;
  /** The CapEx answer, when it was asked (ADR 0036, ruling 7). */
  capex?: string | null;
  /** TH-03's second step: who released a reimbursement to a director (Oct 15). */
  releasedBy?: string | null;
  /** Approved, paying a director, not yet released by another (the screen's Release button). */
  needsRelease?: boolean;
  /** The receipt rule's sentence when it holds this one (Rebuild 46, er1x): Approve grays with it. */
  noReceipt?: string | null;
  kind: 'cash' | 'mileage';
  spentBy: string | null;
  spentOn: string;
  where: string;
  what: string;
  cents: number;
  miles: number | null;
  rateCentsPerMile: number | null;
  accountId: string | null;
  areaId: string | null;
  paidFromAccountId: string | null;
  /** Paid from festival cash: coded and tracked, never owed to the person (R-A). */
  festivalCash: boolean;
  document: Doc | null;
  approvedBy: string | null;
  postedEntryId: string | null;
  /** Denied or canceled, with the sentence the person who spent it reads (item 6). */
  endedAs?: 'denied' | 'canceled' | null;
  endedBy?: string | null;
  endedAt?: string | null;
  endedReason?: string | null;
}

/** MM-DD as the canvas says it: "March 1". */
export function monthDay(onDate: string): string {
  const months = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ];
  const m = Number(onDate.slice(0, 2));
  return `${months[m - 1] ?? onDate} ${Number(onDate.slice(3))}`;
}

/** The cash & bank doors. */
export function cashDoors(me: Me): {
  countroom: boolean;
  deposit: boolean;
  reconcile: boolean;
  viewBank: boolean;
  close: boolean;
  importer: boolean;
} {
  const p = personOf(me);
  // The count room's door is year-scoped: the season the current fiscal year closes.
  const season = fiscalYearToday() - 1;
  return {
    countroom: can(p, 'finance.countroom', { write: true, festivalYear: season }).allowed,
    deposit: can(p, 'finance.deposit', { write: true }).allowed,
    reconcile: can(p, 'finance.reconcile', { write: true }).allowed,
    viewBank: can(p, 'finance.view_bank').allowed,
    close: can(p, 'finance.close', { write: true }).allowed,
    importer: can(p, 'finance.import', { write: true }).allowed,
  };
}

export interface Source {
  id: string;
  name: string;
  kind: string;
  accountId: string | null;
  seedCents: number | null;
  oneId: string | null;
}
export interface CountSheet {
  id: string;
  sourceId: string;
  festivalDay: string;
  dayName: string;
  runner: string | null;
  bankBagNumber: string | null;
  denominations: Record<string, number>;
  checkCents: number;
  grossCents: number;
  seedReturnedCents: number;
  netCents: number;
  counters: { id?: string | null; name: string }[];
  state: string;
  notes: string | null;
  source: string;
  createdAt: string;
}
export interface Seal {
  id: string;
  festivalDay: string;
  countSheetIds: string[];
  grossCents: number;
  seedReturnedCents: number;
  netCents: number;
  createdBy: string | null;
  createdAt: string;
  reopensId: string | null;
}
export interface SeedBag {
  id: string;
  sourceId: string;
  festivalDay: string;
  mix: { bills1: number; bills5: number; bills10: number; bills20: number };
  issuedCents: number;
  state: string;
  returnedCents: number | null;
  varianceCents: number | null;
  varianceCounters: { name: string }[] | null;
}
export interface Deposit {
  id: string;
  depositDate: string;
  bankAccountId: string;
  sourceId: string | null;
  kind: string;
  cents: number;
  memo: string;
  sealId: string | null;
  bankBagNumber: string | null;
  entryId: string | null;
}
export interface BankAccount {
  id: string;
  name: string;
  accountId: string;
  last4: string | null;
}
export interface BankMonth {
  id: string;
  bankAccountId: string;
  month: string;
  state: string;
  statementDocumentId: string | null;
}
export interface BankLine {
  id: string;
  lineDate: string;
  memo: string;
  cents: number;
  reference: string | null;
  lineNo: number;
  groupId: string | null;
  ruleId: string | null;
  entryId: string | null;
}
export interface OurRow {
  kind: 'deposit' | 'check' | 'entry';
  id: string;
  cents: number;
}
export interface BankRule {
  id: string;
  name: string;
  memoContains: string;
  accountId: string;
  approvedBy: string | null;
  active: boolean;
}
export interface ReclassRow {
  id: string;
  entryId: string;
  fromAccountId: string;
  toAccountId: string;
  cents: number;
  reason: string;
  draftId: string;
  createdBy: string | null;
  createdAt: string;
}
export interface RegisterRow {
  entryId: string;
  entryDate: string;
  memo: string | null;
  debitCents: number;
  creditCents: number;
  runningCents: number;
}

/** A report as the API shapes it: sections of rows; money columns hold cents. */
export type Cell = string | number | null;
export interface ReportSection {
  head: string;
  sub?: string;
  columns: string[];
  money: number[];
  rows: Cell[][];
  totals?: Cell[];
}
export interface Report {
  kind: string;
  title: string;
  engine: string;
  asOf: string;
  says: string;
  status: 'ok' | 'NO DATA';
  sections: ReportSection[];
}
export const REPORTS: { kind: string; title: string; takes: 'fy' | 'asOf' | 'year' }[] = [
  // One page per festival day: what came in, by area, in cash and cards (the canvas's first).
  { kind: 'daily-statement', title: 'The daily statement', takes: 'fy' },
  { kind: 'pl-year', title: 'Profit and loss by fiscal year', takes: 'fy' },
  { kind: 'pl-area', title: 'Profit and loss by area', takes: 'fy' },
  { kind: 'pl-month', title: 'Profit and loss by month', takes: 'fy' },
  { kind: 'balance-sheet', title: 'Balance sheet', takes: 'asOf' },
  { kind: 'trial-balance', title: 'Trial balance', takes: 'fy' },
  { kind: 'gl', title: 'General ledger', takes: 'fy' },
  { kind: 'ap-aging', title: 'A/P aging', takes: 'asOf' },
  { kind: '1099', title: '1099 summary', takes: 'year' },
  { kind: 'gaming', title: 'Charitable gaming report', takes: 'year' },
  { kind: 'one-pager', title: 'The directors’ one-pager', takes: 'fy' },
  { kind: 'school-board', title: 'School-board distribution report', takes: 'fy' },
  { kind: 'budget-vs-actual', title: 'Budget vs actual', takes: 'fy' },
  // The shape the budget will take, against chart v3 as it stands — pending James's markup.
  { kind: 'budget-chart-v3', title: 'The budget by account (chart v3, pending)', takes: 'fy' },
  // The 2026 wrap's A9: sign-ups per week by source and by hour; presale and sponsor money by week.
  { kind: 'season-by-week', title: 'The season by week', takes: 'year' },
  // F4: the forum handout — the public version, this year and last; the bookkeeper's is P&L by year.
  { kind: 'forum', title: 'The forum P&L and distribution (public)', takes: 'fy' },
];
export function reportQuery(kind: string, fy: number, asOf: string): string {
  const r = REPORTS.find((x) => x.kind === kind);
  if (r?.takes === 'asOf') return `asOf=${asOf}`;
  if (r?.takes === 'year') return `year=${String(fy - 1)}`;
  return `fy=${String(fy)}`;
}
export interface ChecklistItem {
  key: string;
  label: string;
  /** NO DATA is not true: it counts as open until a director marks it not applicable, with a reason. */
  state: 'done' | 'open' | 'no_data' | 'waived';
  says: string;
  waiver?: { reason: string; by: string; at: string };
}
export const openLines = (items: readonly ChecklistItem[]): number =>
  items.filter((i) => i.state === 'open' || i.state === 'no_data').length;
export interface Checklist {
  scope: 'month' | 'year';
  of: string;
  items: ChecklistItem[];
  passes: boolean;
  says: string;
}

export function budgetDoors(me: Me): { edit: boolean; mapping: boolean; catalog: boolean } {
  const p = personOf(me);
  return {
    edit: can(p, 'finance.budget.edit', { write: true }).allowed,
    mapping: can(p, 'square.mapping.manage', { write: true }).allowed,
    catalog: can(p, 'square.catalog.read', { write: true }).allowed,
  };
}
export interface BaselineRow {
  accountId: string;
  areaId: string | null;
  accountNumber: string;
  accountName: string;
  accountType: string;
  years: { fiscalYear: number; cents: number }[];
  history: { fiscalYear: number; cents: number }[];
  trend: 'up' | 'down' | 'steady' | 'uneven' | 'thin';
  proposedCents: number;
  why: string;
  lastReason: string | null;
}
/**
 * What a budget line is called, area and all.
 *
 * A budget line carries the area of the journal lines it was built from, so one account can
 * hold two of them — the festival's Advertising and the Bakery's. Both screens had said
 * "5010 · Advertising" for each, which made two rows nobody could tell apart and, worse, two
 * form controls with the same accessible name: a director editing one had no way of knowing
 * which, and a screen reader read them identically.
 */
interface NamedLine {
  accountNumber: string;
  accountName: string;
  areaId: string | null;
  areaName: string | null;
}
/** The area a line belongs to, in words — or nothing, where it belongs to no area. */
const areaPart = (l: NamedLine): string => {
  if (!l.areaId) return '';
  const area = (l.areaName ?? '').trim();
  return ` · ${area === '' ? 'an area no longer here' : area}`;
};
/** What a form control on the line is called: the way an aria-label reads it, no middle dot. */
export const budgetLineName = (l: NamedLine): string =>
  `${l.accountNumber} ${l.accountName}${areaPart(l)}`;
/** What the screen prints in the Line cell: the number, a middle dot, the name, the area. */
export const budgetLineText = (l: NamedLine): string =>
  `${l.accountNumber} · ${l.accountName}${areaPart(l)}`;

export interface BudgetRow {
  id: string;
  accountId: string;
  areaId: string | null;
  /** The area's name — a screen prints this and never the id. */
  areaName: string | null;
  accountNumber: string;
  accountName: string;
  accountType: string;
  /** The owner is a role (ADR 0036): the finance director's cost line, the Directors' receipt. */
  ownerRole: 'finance_director' | 'directors' | null;
  baseline: {
    years: { fiscalYear: number; cents: number }[];
    trend: string;
    why: string;
    lastReason: string | null;
  };
  proposedCents: number;
  budgetCents: number;
  amendedCents: number;
  lineCents: number;
  committedCents: number;
  spentCents: number;
  remaining: { remainingCents: number | null; says: string };
  variance: { varianceCents: number; state: 'over' | 'on track' | 'nothing yet'; says: string };
  /** 6790's Year-settings cap (Rebuild 41): remaining is counted against it when it is under the budget. */
  capCents?: number | null;
  note: string | null;
  varianceReason: string | null;
}
export interface BudgetHead {
  id: string;
  fiscalYear: number;
  state: 'proposed' | 'draft' | 'locked' | 'amended' | 'closed';
  locksOn: string | null;
  lockedAt: string | null;
}
export interface AmendmentRow {
  id: string;
  lineId: string;
  deltaCents: number;
  reason: string;
  approvedBy: string | null;
  createdBy: string | null;
  createdAt: string;
}
export interface Director {
  id: string;
  name: string;
  role: string;
}
export interface Mapping {
  id: string;
  squareObjectId: string | null;
  squareName: string | null;
  squarePriceCents: number | null;
  ourName: string | null;
  areaId: string | null;
  register: string | null;
  accountId: string | null;
  state: 'matched' | 'ours_only' | 'square_only' | 'retired';
  changes: {
    what: string;
    priceCents: number | null;
    askedBy: string;
    askedAt: string;
    appliedAt: string | null;
  }[];
  lastSeenAt: string | null;
}
export interface SquareStatus {
  configured: boolean;
  environment: string | null;
  link: {
    locationId: string;
    environment: string;
    pulledTo: string | null;
    enabledBy: string;
  } | null;
  says: string;
  interval: { festival: number; otherwise: number };
}

/** What every import screen's GET now carries beside its card (`previews.ts`). */
export interface PreviewMeta {
  state: 'asked' | 'computing' | 'ready' | 'failed';
  says: string;
  computedAt: string | null;
  tookMs: number | null;
  working: boolean;
  source: string;
  /** When it was asked for — printed beside a failure, so "failed" carries a time. */
  askedAt: string | null;
}

/**
 * Ask again while an import preview is being worked out — and only while.
 *
 * The rule (James, from the staging box's log, 2026-09-27): wait for the answer before asking
 * again, and back off — 2 s, then 5 s, then 10 s. The screens used to re-fetch on every live
 * event, which during an import is every row it writes, and each fetch recomputed the whole
 * preview; the calls piled up until the rest of CIDER was queued behind them.
 */
export function usePreviewPoll(
  meta: PreviewMeta | null | undefined,
  reload: () => void,
  /**
   * Whether the read is in flight. The loop is driven by this, and that is the whole of
   * "never overlapping": while a read is out there is no timer, and the next wait starts only
   * once the answer is in. Without it the effect's own dependencies never change while the
   * work runs, so the page asks exactly once and then sits there — which is what walk 35 found.
   */
  loading = false,
): void {
  const working = meta?.working === true;
  const tries = useRef(0);
  useEffect(() => {
    if (!working) {
      tries.current = 0;
      return;
    }
    if (loading) return; // wait for the answer, then back off
    const waits = [2_000, 5_000, 10_000];
    const wait = waits[Math.min(tries.current, waits.length - 1)] ?? 10_000;
    const t = window.setTimeout(() => {
      tries.current += 1;
      reload();
    }, wait);
    return () => {
      window.clearTimeout(t);
    };
  }, [working, loading, reload]);
}

/** A page of a list, and how many there are altogether. Every list endpoint answers in this shape. */
export interface Paged<T> {
  total: number;
  offset: number;
  limit: number;
  rows: T[];
}

/** The page every list screen asks for first. */
export const PAGE = 200;

/**
 * A list's caption: **the count first, then what is on screen**.
 *
 * "200 PEOPLE" on a box holding 1,340 of them is a page size wearing a count's clothes, and it
 * stops at the H's with no way to the rest (James, from staging, 2026-09-27). So a caption says
 * the total, and says how much of it you are looking at when that is less.
 */
export function captionFor(
  total: number,
  showing: number,
  asked: string,
  one: string,
  many: string,
): string {
  const n = formatCount(total);
  const word = total === 1 ? one : many;
  const toward = asked ? ` for “${asked}”` : '';
  return showing < total
    ? `${n} ${word}${toward} · showing ${formatCount(showing)}`
    : `${n} ${word}${toward}`;
}

/**
 * An engine's name in words — `gross_taken_by_area` → "Gross taken by area",
 * `ledger.profitAndLoss (byArea)` → "Ledger profit and loss (by area)". Every report names the
 * engine it read (rule 2); a director reads it as words, never as code (overnight 6, 2f).
 */
export function engineSays(engine: string): string {
  const w = engine
    .replace(/[._]/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .toLowerCase()
    .trim();
  return w.charAt(0).toUpperCase() + w.slice(1);
}

/** A budget line's owner in words (ADR 0036) — a role, never a person. */
export const ownerWords = (role: 'finance_director' | 'directors' | null): string =>
  role === 'directors'
    ? 'the Directors'
    : role === 'finance_director'
      ? 'the finance director'
      : 'nobody yet';
/** The one owner a line's account can have: receipts are the Directors', costs the finance director's. */
export const ownerFor = (accountType: string): 'finance_director' | 'directors' =>
  accountType === 'income' ? 'directors' : 'finance_director';
